import { useState, useEffect } from 'react';
import { X, Send, Loader, Paperclip, Link as LinkIcon } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface EmailMessage {
  id: string;
  from_addr: string;
  to_addr?: string;
  subject: string;
  body_text?: string;
  body_html?: string;
  received_at: string;
  direction: 'in' | 'out';
  gmail_thread_id: string;
  creator_id?: string;
}

interface Collaboration {
  id: string;
  name: string;
  brand: string;
  date: string;
  contract_signed: boolean;
  post_progress: string;
  link_token: string;
}

interface EmailReplyComposerProps {
  threadMessages: EmailMessage[];
  creatorEmail: string;
  creatorName: string;
  creatorId?: string;
  managerEmail?: string;
  isManagerView?: boolean;
  collaborations?: Collaboration[];
  onClose: () => void;
  onSend: (params: {
    subject: string;
    body: string;
    attachments: File[];
  }) => Promise<any>;
}

export default function EmailReplyComposer({
  threadMessages,
  creatorEmail,
  creatorName,
  creatorId,
  managerEmail,
  isManagerView = false,
  collaborations = [],
  onClose,
  onSend,
}: EmailReplyComposerProps) {
  const [draftSubject, setDraftSubject] = useState('');
  const [draftContent, setDraftContent] = useState('');
  const [attachments, setAttachments] = useState<File[]>([]);
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [sending, setSending] = useState(false);
  const [showCollabMenu, setShowCollabMenu] = useState(false);

  useEffect(() => {
    const lastMessage = threadMessages[threadMessages.length - 1];
    if (lastMessage) {
      const subject = lastMessage.subject.startsWith('Re:')
        ? lastMessage.subject
        : `Re: ${lastMessage.subject}`;
      setDraftSubject(subject);
    }

  }, [threadMessages]);

  const generateAIDraft = async () => {
    setGeneratingDraft(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('Non authentifié');

      const recentMessages = threadMessages.slice(-5);

      const threadContext = recentMessages
        .map(msg => {
          const bodyText = msg.body_text || msg.body_html || '';
          const truncatedBody = bodyText.length > 1000 ? bodyText.substring(0, 1000) + '...' : bodyText;
          return `[${msg.direction === 'in' ? 'Reçu' : 'Envoyé'}] ${msg.from_addr}:\n${truncatedBody}`;
        })
        .join('\n\n---\n\n');

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-generate-draft`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            thread_context: threadContext,
            sender_name: creatorName,
            action_type: 'reply_as_manager',
            creator_name: creatorName,
            creator_id: creatorId,
            manager_name: managerEmail?.split('@')[0] || 'Manager',
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Erreur API:', errorText);
        try {
          const errorJson = JSON.parse(errorText);
          throw new Error(errorJson.error || 'Erreur lors de la génération du brouillon');
        } catch {
          throw new Error('Erreur lors de la génération du brouillon');
        }
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let accumulated = '';

      if (reader) {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const chunk = decoder.decode(value);
          accumulated += chunk;
          setDraftContent(accumulated);
        }
      }
    } catch (error) {
      console.error('Erreur génération IA:', error);
      const errorMessage = error instanceof Error ? error.message : 'Erreur inconnue';
      setDraftContent(`Erreur lors de la génération: ${errorMessage}\n\nVeuillez réessayer.`);
    } finally {
      setGeneratingDraft(false);
    }
  };

  const handleSend = async () => {
    if (!draftContent.trim()) return;

    setSending(true);
    try {
      const result = await onSend({
        subject: draftSubject,
        body: draftContent,
        attachments,
      });

      onClose();
    } catch (error) {
      console.error('Erreur envoi:', error);
      setSending(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setAttachments(Array.from(e.target.files));
    }
  };

  const insertCollaborationLink = (collab: Collaboration) => {
    const baseUrl = window.location.origin;
    const trackingUrl = `${baseUrl}/suivi/${collab.link_token}`;
    const linkText = `\n\nVoici le lien de suivi pour la campagne "${collab.name}" :\n${trackingUrl}\n`;

    setDraftContent(prev => prev + linkText);
    setShowCollabMenu(false);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold">Répondre</h3>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="p-4 border-b space-y-3">
          <div>
            <label className="text-sm font-medium text-gray-700">Objet</label>
            <input
              type="text"
              value={draftSubject}
              onChange={(e) => setDraftSubject(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          <div className="text-sm text-gray-600">
            Envoyé depuis le compte de <strong>{creatorName}</strong>, au nom du manager
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          {!draftContent && !generatingDraft && (
            <div className="flex items-center justify-center h-full">
              <button
                onClick={generateAIDraft}
                className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all shadow-sm hover:shadow-md font-medium"
              >
                Générer une réponse
              </button>
            </div>
          )}

          {generatingDraft && (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Loader className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-3" />
                <p className="text-sm text-gray-600">Génération en cours...</p>
              </div>
            </div>
          )}

          {draftContent && (
            <textarea
              value={draftContent}
              onChange={(e) => setDraftContent(e.target.value)}
              className="w-full h-full min-h-[300px] p-3 border rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
              placeholder="Écrivez votre message..."
            />
          )}
        </div>


        <div className="p-4 border-t flex items-center justify-between">
          <div className="flex items-center gap-2">
            <label className="cursor-pointer flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg">
              <Paperclip className="h-4 w-4" />
              <span>Pièce jointe</span>
              <input
                type="file"
                multiple
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {attachments.length > 0 && (
              <span className="text-sm text-gray-600">
                {attachments.length} fichier{attachments.length > 1 ? 's' : ''}
              </span>
            )}

            <div className="relative">
              <button
                onClick={() => setShowCollabMenu(!showCollabMenu)}
                disabled={collaborations.length === 0}
                className="flex items-center gap-2 px-3 py-2 text-sm text-blue-700 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                title={collaborations.length === 0 ? 'Aucune collaboration active' : `${collaborations.length} collaboration(s) disponible(s)`}
              >
                <LinkIcon className="h-4 w-4" />
                <span>Lien campagne ({collaborations.length})</span>
              </button>

              {showCollabMenu && collaborations.length > 0 && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowCollabMenu(false)}
                  />
                  <div className="absolute bottom-full left-0 mb-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-20 max-h-96 overflow-y-auto">
                    <div className="p-2">
                      <div className="text-xs font-semibold text-gray-600 px-2 py-1 mb-1">
                        Sélectionnez une campagne
                      </div>
                      {collaborations.map((collab) => (
                        <button
                          key={collab.id}
                          onClick={() => insertCollaborationLink(collab)}
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 rounded transition-colors"
                        >
                          <div className="text-sm font-medium text-gray-900 mb-0.5">
                            {collab.name}
                          </div>
                          <div className="text-xs text-gray-500">
                            {collab.brand}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                              collab.post_progress === '100'
                                ? 'bg-green-100 text-green-700'
                                : collab.contract_signed
                                ? 'bg-blue-100 text-blue-700'
                                : 'bg-yellow-100 text-yellow-700'
                            }`}>
                              {collab.post_progress === '100' ? 'Publié' : collab.contract_signed ? 'Signé' : 'En attente'}
                            </span>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2">
            {draftContent && !generatingDraft && (
              <button
                onClick={generateAIDraft}
                className="px-4 py-2 text-sm text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
              >
                Régénérer
              </button>
            )}
            <button
              onClick={handleSend}
              disabled={!draftContent.trim() || sending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? <Loader className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              Envoyer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
